import * as constants from "@/config/constants";
import {
	customBehaviorByMobileTargetedPath,
	customBehaviorByPathsWithFilter,
} from "@/config/customBehaviorByPath";
import * as irConstants from "@/config/site/insuranceranked";
import { move, path, pathOr } from "ramda";

export const replacePhoneNumbers = () => {
	if (window.Invoca) {
		window.Invoca.PNAPI.run();
	}
};

export const resolveRatingKey = (
	vertical,
	filter,
	filteringInformation,
	isMobileTargetedPath,
) => {
	const isNotUserFiltered =
		filteringInformation[constants.IS_NOT_USER_FILTERED];

	/* boolean for determining if a vertical uses mobileRating */
	const isMobileRatingUsedForMobileTargetedPaths = pathOr(
		false,
		[vertical, "isMobileRatingUsedForMobileTargetedPaths"],
		constants.SITE_INFORMATION,
	);

	/* If a vertical implements sorting by mobileRating, browsing a mobile targeted path, and no filters have been applied*/
	if (
		isMobileRatingUsedForMobileTargetedPaths &&
		isMobileTargetedPath &&
		isNotUserFiltered
	) {
		return "mobileRating";
	} else {
		// Default key for finding the rating of a company by filter. Defaults to rating if it's not found
		const defaultOrUrlPathFilterRatingKey = pathOr(
			"rating",
			[constants.SITE, vertical, filter, constants.CUSTOM_RATING_KEY],
			customBehaviorByPathsWithFilter,
		);

		const creditQuality = filteringInformation.creditQuality;
		const mortgageType = filteringInformation.mortgageType;
		const isAged = pathOr(
			false,
			[irConstants.AGED_FILTERING_KEY],
			filteringInformation,
		);
		const olderThan50FilteringKey = isAged
			? irConstants.AGED_FILTERING_KEY
			: undefined;
		const keyPath = creditQuality || olderThan50FilteringKey || mortgageType;

		return pathOr(
			defaultOrUrlPathFilterRatingKey,
			[
				constants.SITE,
				vertical,
				constants.CUSTOM_RATING_KEY_BY_UI_FILTERING,
				keyPath,
			],
			customBehaviorByPathsWithFilter,
		);
	}
};

const sortOrganic = (companies, ratingKey) =>
	companies.sort(
		(a, b) => path(["fields", ratingKey], b) - path(["fields", ratingKey], a),
	);

const applyPositionOverride = (companies, overrideCompanySlug) => {
	if (!overrideCompanySlug) {
		return companies;
	}
	const originalPosition = companies.findIndex(
		(company) => company.fields.companySlug === overrideCompanySlug,
	);
	if (originalPosition !== -1) {
		return move(originalPosition, 0, companies);
	}

	return companies;
};

const sortMediaAlphaCompanies = (companies, ratingKey) => {
	const mediaAlphaCompanies = companies.filter(
		(company) => company.fields.isMediaAlpha || company.fields.is_direct,
	); // sort direct partners in MA payload as if they're MA. Allows sorting by bid
	const organicCompanies = companies.filter(
		(company) => !company.fields.isMediaAlpha && !company.fields.is_direct,
	);
	const sortedMediaAlphaCompanies = mediaAlphaCompanies.sort(
		(a, b) => path(["fields", "bid"], b) - path(["fields", "bid"], a),
	);
	return [
		...sortedMediaAlphaCompanies,
		...sortOrganic(organicCompanies, ratingKey),
	];
};

/**
 * Main company sort function used when displaying company list
 * @returns
 */
export const sortCompanies = (
	vertical,
	filter,
	filteringInformation,
	companies,
	isMobileTargetedPath,
	sortCompaniesByMediaAlphaBid,
	companyPositionOverride,
) => {
	const ratingKey = resolveRatingKey(
		vertical,
		filter,
		filteringInformation,
		isMobileTargetedPath,
	);
	if (sortCompaniesByMediaAlphaBid) {
		return sortMediaAlphaCompanies(companies, ratingKey);
	}

	const organicallySortedCompanies = [...sortOrganic(companies, ratingKey)];
	const positionOverrideCompanies = applyPositionOverride(
		organicallySortedCompanies,
		companyPositionOverride,
	);

	// exclude any companies with rating of 0
	// useful in verticals where we have per-path rankings (ex. life insurance over 50 toggle)
	const companiesWithValidRating = positionOverrideCompanies.filter(
		(company) => path(["fields", ratingKey], company) > 0,
	);

	return companiesWithValidRating;
};

export const resolveMainBenefitsField = (
	vertical,
	filter,
	isMobileTargetedPath,
	filteringInformation,
	company,
) => {
	const { creditQuality, isNotUserFiltered } = filteringInformation;
	const isFilteringApplied = !isNotUserFiltered;
	/* Function to make this more extensible/using different UI filters to dynamically change Company Main Benefits fields */
	const returnUiFilteringCompanyBenefitsFieldPath = (uiFilter) => [
		constants.SITE,
		vertical,
		constants.CUSTOM_MAIN_BENEFITS_FIELD_BY_UI_FILTERING,
		uiFilter,
	];
	const uiFilteredCompanyBenefitsFieldPath =
		returnUiFilteringCompanyBenefitsFieldPath(creditQuality);

	const urlPathFilteredCompanyBenefitsFieldPath = [
		constants.SITE,
		vertical,
		filter,
		constants.CUSTOM_MAIN_BENEFITS_FIELD,
	];

	const mobileCompanyMainBenefitsField = [
		constants.SITE,
		vertical,
		constants.CUSTOM_MAIN_BENEFITS_FIELD,
	];

	const isMobileFieldDefinedInConfig = !!path(
		mobileCompanyMainBenefitsField,
		customBehaviorByMobileTargetedPath,
	);
	const isMobileFieldPresentInCompany = !!path(
		["fields", "mobileCompanyMainBenefits"],
		company,
	);

	// Use mobile benefits when applicable even when there's a url path filer e.g. mobileCompanyMainBenefits
	const shouldUseMobileBenefits =
		isMobileTargetedPath &&
		isMobileFieldDefinedInConfig &&
		isMobileFieldPresentInCompany;

	/*
  Fallback logic to use targeted slug for benefits field path when mobile can't be used (config doesn't exit or company doesn't have 'mobileCompanyMainBenefits')

  This logic means that mobileCompanyMainBenefits takes precedence over a custom field based on targeted slugs when applicable.

  An example would be `/personal-loans/a/bad-credit` where a company that has a mobileCompanyMainBenefits field with data will be used,
  otherwise it falls back to using the 'poorCreditCompanyMainBenefits'
  */
	const benefitsFieldPathByUrl = shouldUseMobileBenefits
		? mobileCompanyMainBenefitsField
		: urlPathFilteredCompanyBenefitsFieldPath;

	const customBehaviorObject = shouldUseMobileBenefits
		? customBehaviorByMobileTargetedPath
		: customBehaviorByPathsWithFilter;

	const benefitsFieldPath = isFilteringApplied
		? uiFilteredCompanyBenefitsFieldPath
		: benefitsFieldPathByUrl;
	return pathOr("companyMainBenefits", benefitsFieldPath, customBehaviorObject);
};
